import api from './api';
import type { PlayerProfile } from './playerProfileService';

// === Embedded Sub-Types ===

export interface ProTeamPlayer {
  playerId?: string | null;
  name?: string | null;
  nickname?: string | null;
  role?: string | null;
  seed?: number | null;
  countryCode?: string | null;
  flag?: string | null;
  profileImage?: string | null;
  worldRanking?: number | null;
  // wenn ?expand=players: vollständiges PlayerProfile
  profile?: PlayerProfile | null;
}

export interface ProTeam {
  id?: string;
  name?: string;
  shortName?: string | null;
  countryCode?: string | null;
  flag?: string | null;
  seed?: number | null;
  group?: string | null;
  isSeeded?: boolean;
  eliminatedInRound?: string | null;
  finalPosition?: number | null;
  players?: ProTeamPlayer[];
  teamImage?: string | null;
  bio?: string | null;
  titlesWithTeam?: number | null;
}

export interface ProMatchSide {
  teamId?: string | null;
  teamName?: string | null;
  score?: number | null;
  average?: number | null;
  highestCheckout?: number | null;
  checkouts100Plus?: number | null;
  nineDarters?: number | null;
  isWinner?: boolean;
}

export interface ProMatch {
  id?: string;
  round?: string;
  roundLabel?: string | null;
  group?: string | null;
  matchNumber?: number | null;
  scheduledAt?: number | null;
  scheduledSession?: string | null;
  venue?: string | null;
  status?: 'SCHEDULED' | 'LIVE' | 'FINISHED' | 'POSTPONED' | 'CANCELLED' | null;
  format?: string | null;
  home?: ProMatchSide;
  away?: ProMatchSide;
  winnerTeamId?: string | null;
  streamUrl?: string | null;
  notes?: string | null;
}

export interface ProPrizeBreakdown {
  position?: string;
  amount?: number | null;
  currency?: string | null;
  perTeam?: boolean;
  count?: number | null;
  bonusType?: string | null;
  note?: string | null;
}

export interface ProPastWinner {
  year?: number;
  winnerTeamName?: string | null;
  winnerPlayerIds?: string[];
  runnerUpTeamName?: string | null;
  score?: string | null;
  venue?: string | null;
  prizeMoney?: number | null;
  currency?: string | null;
  note?: string | null;
}

export interface ProKeyFact {
  icon?: string | null;
  label?: string;
  value?: string;
  sublabel?: string | null;
  order?: number;
}

export interface ProScheduleEntry {
  date?: number | null;
  dateLabel?: string | null;
  sessionLabel?: string | null;
  round?: string | null;
  description?: string | null;
  matchIds?: string[];
}

export interface ProRecord {
  label?: string;
  value?: string;
  holder?: string | null;
  holderPlayerId?: string | null;
  year?: number | null;
  note?: string | null;
}

export interface ProFormatRule {
  label?: string;
  value?: string;
  order?: number;
}

export interface ProNotableNineDarter {
  year?: number | null;
  playerId?: string | null;
  playerName?: string | null;
  round?: string | null;
  opponent?: string | null;
  note?: string | null;
}

export interface ProQualificationCriterion {
  label?: string;
  value?: string;
  order?: number;
  count?: number | null;
}

export interface ProParallelCompetition {
  name?: string;
  description?: string | null;
  prizeMoney?: number | null;
  prizeMoneyLabel?: string | null;
  winnerName?: string | null;
  participants?: string[];
}

export interface ProSponsorHistoryEntry {
  year?: number;
  sponsor?: string | null;
  prizeMoney?: number | null;
}

export interface ProDrawPairing {
  round?: string | null;
  homeTeamName?: string | null;
  awayTeamName?: string | null;
  homePlayerId?: string | null;
  awayPlayerId?: string | null;
  order?: number;
}

export interface ProBroadcaster {
  name?: string | null;
  countryCode?: string | null;
  url?: string | null;
  logo?: string | null;
}

export interface ProGroup {
  name?: string | null;
  teamIds?: string[];
}

// === Main Interface ===

export interface ProTournamentInfo {
  _id?: string;
  id?: string;
  slug?: string;
  createdAt?: number;
  updatedAt?: number | null;

  name?: string;
  shortName?: string | null;
  edition?: string | null;
  year?: number | null;
  organization?: string;
  tier?: string | null;
  tournamentType?: 'TEAM' | 'SINGLES' | 'PAIRS' | 'MIXED' | string | null;
  discipline?: string | null;

  status?: 'UPCOMING' | 'LIVE' | 'COMPLETED' | 'CANCELLED' | null;
  visible?: boolean;
  featured?: boolean;

  startDate?: number | null;
  endDate?: number | null;
  dateLabel?: string | null;

  venue?: string | null;
  city?: string | null;
  country?: string | null;
  countryCode?: string | null;
  capacity?: number | null;

  totalPrizeMoney?: number | null;
  prizeMoneyCurrency?: string | null;
  prizeMoneyLabel?: string | null;
  prizeBreakdown?: ProPrizeBreakdown[];

  formatSummary?: string | null;
  formatRules?: ProFormatRule[];

  keyFacts?: ProKeyFact[];

  titleHolderTeamName?: string | null;
  titleHolderPlayerIds?: string[];

  records?: ProRecord[];

  teams?: ProTeam[];
  totalTeams?: number | null;

  hasGroupStage?: boolean;
  groups?: ProGroup[];

  matches?: ProMatch[];

  schedule?: ProScheduleEntry[];

  pastWinners?: ProPastWinner[];
  firstHeldYear?: number | null;

  trophyName?: string | null;
  trophyImage?: string | null;
  notableNineDarters?: ProNotableNineDarter[];
  qualificationCriteria?: ProQualificationCriterion[];
  parallelCompetitions?: ProParallelCompetition[];
  sponsorHistory?: ProSponsorHistoryEntry[];
  drawPairings?: ProDrawPairing[];

  logo?: string | null;
  heroImage?: string | null;
  thumbnail?: string | null;
  sponsor?: string | null;
  sponsorLogo?: string | null;

  overview?: string | null;
  intro?: string | null;

  broadcasters?: ProBroadcaster[];
  officialWebsite?: string | null;

  seoTitle?: string | null;
  seoDescription?: string | null;
  seoKeywords?: string[];

  tags?: string[];
  views?: number;
  relatedTournamentSlugs?: string[];

  // Aus expand=players resolved
  _expandedPlayers?: boolean;
}

export interface ProTournamentListResponse {
  tournaments?: ProTournamentInfo[];
}

/**
 * Client-Fallback: Effektiven Status aus der Timeline ableiten (Spiegel der
 * Backend-Logik deriveEffectiveStatus). Nötig, weil gecachte/alte Backend-
 * Antworten den Status nicht nachziehen — z.B. stand der World Cup of Darts
 * nach Turnier-Ende noch wochenlang auf UPCOMING.
 */
export const deriveEffectiveStatus = (
  t: Pick<ProTournamentInfo, 'status' | 'startDate' | 'endDate'> | null | undefined,
  now: number = Date.now()
): ProTournamentInfo['status'] => {
  const status = t?.status ?? undefined;
  if (!t || status === 'CANCELLED') return status;
  const DAY_MS = 24 * 60 * 60 * 1000;
  const endDate = typeof t.endDate === 'number' ? t.endDate : null;
  const startDate = typeof t.startDate === 'number' ? t.startDate : null;
  if (endDate !== null && endDate + DAY_MS < now) return 'COMPLETED';
  if (
    status === 'UPCOMING' &&
    startDate !== null &&
    startDate <= now &&
    (endDate === null || now <= endDate + DAY_MS)
  ) {
    return 'LIVE';
  }
  return status;
};

const withEffectiveStatus = (t: ProTournamentInfo): ProTournamentInfo => ({
  ...t,
  status: deriveEffectiveStatus(t),
});

class ProTournamentInfoService {
  async list(params?: {
    limit?: number;
    offset?: number;
    status?: string;
    organization?: string;
    year?: number;
    search?: string;
  }): Promise<ProTournamentInfo[]> {
    try {
      const res = await api.get<ProTournamentListResponse>('/api/pro-tournaments', {
        params: {
          limit: params?.limit ?? 50,
          offset: params?.offset ?? 0,
          status: params?.status,
          organization: params?.organization,
          year: params?.year,
          search: params?.search,
        },
      });
      return (res.data?.tournaments ?? []).map(withEffectiveStatus);
    } catch (err) {
      console.error('Fehler beim Laden der ProTournamentInfo-Liste:', err);
      return [];
    }
  }

  async getById(id: string, options?: { expand?: boolean }): Promise<ProTournamentInfo | null> {
    if (!id) return null;
    try {
      const res = await api.get<ProTournamentInfo>(`/api/pro-tournament/id/${id}`, {
        params: { expand: options?.expand ? 'players' : undefined },
      });
      return res.data ? withEffectiveStatus(res.data) : null;
    } catch (err) {
      console.error('Fehler beim Laden der ProTournamentInfo by ID:', err);
      return null;
    }
  }

  async getBySlug(slug: string, options?: { expand?: boolean }): Promise<ProTournamentInfo | null> {
    if (!slug) return null;
    try {
      const res = await api.get<ProTournamentInfo>(`/api/pro-tournament/slug/${slug}`, {
        params: { expand: options?.expand ? 'players' : undefined },
      });
      return res.data ? withEffectiveStatus(res.data) : null;
    } catch (err) {
      console.error('Fehler beim Laden der ProTournamentInfo by Slug:', err);
      return null;
    }
  }

  // === Helpers ===
  displayName(t?: ProTournamentInfo | null): string {
    if (!t) return '';
    const base = t.name || t.shortName || '';
    if (t.edition) return `${base} ${t.edition}`.trim();
    return base;
  }

  formatPrize(t?: ProTournamentInfo | null): string {
    if (!t) return '';
    if (t.prizeMoneyLabel) return t.prizeMoneyLabel;
    if (typeof t.totalPrizeMoney === 'number') {
      const currency = t.prizeMoneyCurrency || '';
      const formatted = t.totalPrizeMoney.toLocaleString('de-DE');
      return currency ? `${currency} ${formatted}` : formatted;
    }
    return '';
  }

  formatAmount(amount?: number | null, currency?: string | null): string {
    if (amount === null || amount === undefined) return '';
    const formatted = amount.toLocaleString('de-DE');
    if (!currency) return formatted;
    if (currency === 'GBP') return `£${formatted}`;
    if (currency === 'EUR') return `€${formatted}`;
    if (currency === 'USD') return `$${formatted}`;
    return `${currency} ${formatted}`;
  }

  getSeededTeams(t?: ProTournamentInfo | null): ProTeam[] {
    if (!t?.teams) return [];
    return t.teams
      .filter((team) => team.isSeeded || (typeof team.seed === 'number' && team.seed > 0))
      .sort((a, b) => (a.seed ?? 999) - (b.seed ?? 999));
  }

  getNonSeededTeams(t?: ProTournamentInfo | null): ProTeam[] {
    if (!t?.teams) return [];
    return t.teams.filter((team) => !team.isSeeded && !team.seed);
  }

  getKeyFactsSorted(t?: ProTournamentInfo | null): ProKeyFact[] {
    if (!t?.keyFacts) return [];
    return [...t.keyFacts].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  getFormatRulesSorted(t?: ProTournamentInfo | null): ProFormatRule[] {
    if (!t?.formatRules) return [];
    return [...t.formatRules].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  getScheduleSorted(t?: ProTournamentInfo | null): ProScheduleEntry[] {
    if (!t?.schedule) return [];
    return [...t.schedule];
  }

  getPastWinnersSorted(t?: ProTournamentInfo | null): ProPastWinner[] {
    if (!t?.pastWinners) return [];
    return [...t.pastWinners].sort((a, b) => (b.year ?? 0) - (a.year ?? 0));
  }

  hasSection(t: ProTournamentInfo | null | undefined, section:
    | 'teams' | 'matches' | 'pastWinners' | 'records' | 'schedule'
    | 'prize' | 'format' | 'groups' | 'broadcasters'
    | 'nineDarters' | 'qualification' | 'parallel' | 'sponsorHistory' | 'draw'
  ): boolean {
    if (!t) return false;
    switch (section) {
      case 'teams': return Array.isArray(t.teams) && t.teams.length > 0;
      case 'matches': return Array.isArray(t.matches) && t.matches.length > 0;
      case 'pastWinners': return Array.isArray(t.pastWinners) && t.pastWinners.length > 0;
      case 'records': return Array.isArray(t.records) && t.records.length > 0;
      case 'schedule': return Array.isArray(t.schedule) && t.schedule.length > 0;
      case 'prize': return Array.isArray(t.prizeBreakdown) && t.prizeBreakdown.length > 0;
      case 'format': return Array.isArray(t.formatRules) && t.formatRules.length > 0;
      case 'groups': return !!t.hasGroupStage && Array.isArray(t.groups) && t.groups.length > 0;
      case 'broadcasters': return Array.isArray(t.broadcasters) && t.broadcasters.length > 0;
      case 'nineDarters': return Array.isArray(t.notableNineDarters) && t.notableNineDarters.length > 0;
      case 'qualification': return Array.isArray(t.qualificationCriteria) && t.qualificationCriteria.length > 0;
      case 'parallel': return Array.isArray(t.parallelCompetitions) && t.parallelCompetitions.length > 0;
      case 'sponsorHistory': return Array.isArray(t.sponsorHistory) && t.sponsorHistory.length > 0;
      case 'draw': return Array.isArray(t.drawPairings) && t.drawPairings.length > 0;
      default: return false;
    }
  }
}

export default new ProTournamentInfoService();
